// Supplier order number block on the engineer's order detail — the supplier's
// own reference, the one the branch counter looks up. Mirrors the mock's
// "numbers to quote at the counter" block (octopus-mock order-details-card).
// For suppliers whose branches look orders up by ticket number (CPS), the
// ticket replaces the order number once the supplier has sent one.
import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'

type CopyState = 'idle' | 'copied' | 'failed'

const COPY_LABEL: Record<CopyState, string> = {
  idle: 'Copy',
  copied: 'Copied',
  failed: "Couldn't copy",
}
const REFERENCE_LABEL = {
  orderNumber: 'Supplier order number',
  ticketNumber: 'Ticket number',
} as const
type ReferenceLabel = (typeof REFERENCE_LABEL)[keyof typeof REFERENCE_LABEL]

const copyAnnouncement = (copyState: CopyState, label: ReferenceLabel): string => {
  if (copyState === 'copied') return `${label} copied`
  if (copyState === 'failed') return `Couldn't copy the ${label.toLowerCase()}`
  return ''
}
/** How long "Copied" / "Couldn't copy" shows before the button resets. */
const COPY_FEEDBACK_MS = 2000
/** Longer references drop a size so they wrap to two lines, not four. */
const LONG_REFERENCE_LENGTH = 14

interface SupplierOrderNumberProps {
  /** The supplier's reference, or null/empty until the supplier issues one. */
  supplierOrderId: string | null | undefined
  /**
   * The supplier has the order and it's still open. Only then is "quote our
   * order number" sound advice — before submission (awaiting approval, not
   * sent, declined, withdrawn) the branch can't find the order, and once it's
   * finished there's nothing left for the branch to accept.
   */
  isOpenWithSupplier: boolean
  /** The branch ticket number; shown instead of the order number when set. */
  ticketNumber?: string | null
}

function CopyButton({ value, label }: { value: string; label: ReferenceLabel }) {
  const [copyState, setCopyState] = useState<CopyState>('idle')
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current)
  }, [])

  const handleCopy = async () => {
    let nextState: CopyState = 'copied'
    try {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable')
      await navigator.clipboard.writeText(value)
    } catch {
      nextState = 'failed'
    }
    setCopyState(nextState)
    if (resetTimer.current) clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS)
  }

  return (
    <>
      {/* Plain button, not the shared <Button>: the mock's compact size relies
          on tailwind-merge letting h-8/px-2.5/text-xs beat the "sm" size, and
          ours joins classes with clsx, so the larger sm sizes would win. These
          are the mock's resolved classes (neutral + compact); focus ring uses
          the tenant brand colour instead of the mock's fixed "ink". */}
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex h-8 flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border-2 border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-900 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 active:scale-[0.98]"
      >
        {copyState === 'copied' ? <Check className="h-4 w-4 flex-shrink-0" aria-hidden /> : <Copy className="h-4 w-4 flex-shrink-0" aria-hidden />}
        {COPY_LABEL[copyState]}
        {copyState === 'idle' && <span className="sr-only"> {label.toLowerCase()}</span>}
      </button>
      <span role="status" className="sr-only">
        {copyAnnouncement(copyState, label)}
      </span>
    </>
  )
}

/**
 * Supplier order number in large type with a Copy button; the mock's "Not
 * issued yet" while the supplier hasn't given one, plus a hint to quote our
 * number only while the supplier has the order open. Shown at every stage.
 */
export function SupplierOrderNumber({ supplierOrderId, isOpenWithSupplier, ticketNumber }: SupplierOrderNumberProps) {
  const ticket = ticketNumber?.trim()
  const reference = ticket || supplierOrderId?.trim()
  const label = ticket ? REFERENCE_LABEL.ticketNumber : REFERENCE_LABEL.orderNumber
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">{label}</p>
        {reference ? (
          <p
            className={`font-bold leading-tight tabular-nums text-gray-900 [overflow-wrap:anywhere] ${
              reference.length > LONG_REFERENCE_LENGTH ? 'text-sm sm:text-base' : 'text-base sm:text-xl'
            }`}
          >
            {reference}
          </p>
        ) : (
          <>
            <p className="text-base font-bold leading-tight text-gray-900">Not issued yet</p>
            {isOpenWithSupplier && (
              <p className="text-xs text-gray-500">Quote our order number until the branch accepts it.</p>
            )}
          </>
        )}
      </div>
      {reference && <CopyButton value={reference} label={label} />}
    </div>
  )
}
